import { pageRouteUrls } from "./pageRouteUrls";

/**
 * Help Center content.
 *
 * Every user-facing operation on the platform is documented here as a single
 * article. The data is intentionally kept in one plain-data file (no JSX) so the
 * Help Center index can search across every article's title, summary, keywords,
 * and body text client-side, and so new articles can be added without touching
 * any page component.
 */

export interface HelpBlock {
    /** Optional section heading. */
    heading?: string;
    /** Free-form paragraphs. */
    body?: string[];
    /** Ordered, numbered how-to steps. */
    steps?: string[];
    /** Unordered bullet points. */
    bullets?: string[];
}

export interface HelpRelatedLink {
    label: string;
    /** In-app route (from pageRouteUrls). */
    url: string;
}

/** Audience roles an article can be tagged with. */
export type HelpRole = "student" | "tutor" | "parent" | "academy";

/** Role filter chips, in display order. */
export const helpRoles: { key: HelpRole; label: string }[] = [
    { key: "student", label: "Student" },
    { key: "tutor", label: "Tutor" },
    { key: "parent", label: "Parent" },
    { key: "academy", label: "Academy" },
];

export interface HelpArticle {
    slug: string;
    title: string;
    category: string;
    /**
     * Roles this article is aimed at. Omit for articles that apply to everyone
     * (they show under every role filter).
     */
    roles?: HelpRole[];
    /** Extra search terms that may not appear verbatim in the text. */
    keywords: string[];
    /** One-line description shown on cards and article headers. */
    summary: string;
    blocks: HelpBlock[];
    related?: HelpRelatedLink[];
}

/** Display order for category groupings on the index page. */
export const helpCategories: string[] = [
    "Getting Started",
    "Account & Profile",
    "Courses",
    "Lectures",
    "Students (Tutors)",
    "Payments & Billing",
    "For Parents",
    "For Academies",
    "Messaging & Notifications",
    "Trust & Safety",
];

export const helpArticles: HelpArticle[] = [
    // ─────────────────────────── Getting Started ───────────────────────────
    {
        slug: "how-the-platform-works",
        title: "How the platform works",
        category: "Getting Started",
        keywords: ["overview", "intro", "start", "guide", "basics"],
        summary: "A quick tour of what you can do as a student, tutor, parent, or academy.",
        blocks: [
            {
                body: [
                    "The platform connects students with tutors and academies through courses and live or recorded lectures. What you can do depends on the role your account was created with.",
                ],
            },
            {
                heading: "Roles at a glance",
                bullets: [
                    "Student — browse and enrol in courses, join lectures, submit homework, take exams, track progress, and earn certificates.",
                    "Tutor — create courses and lectures, run live sessions, set homework and exams, issue certificates, manage enrolled students, and send invoices for payment.",
                    "Parent — link to a child's account to follow their courses, progress, and reports.",
                    "Academy — manage a team of tutors, publish courses, and view combined reports.",
                ],
            },
            {
                heading: "Where to go first",
                steps: [
                    "Create your account and choose the role that fits you.",
                    "Verify your email so you can receive notifications.",
                    "Complete your profile from the settings page for your role.",
                    "Open your Dashboard for a summary of your activity.",
                ],
            },
        ],
        related: [
            { label: "Create an account", url: pageRouteUrls.Register() },
            { label: "Open your Dashboard", url: pageRouteUrls.Dashboard() },
        ],
    },
    {
        slug: "create-an-account",
        title: "Create an account",
        category: "Getting Started",
        keywords: ["register", "sign up", "signup", "join", "new account", "role"],
        summary: "Register as a student, tutor, parent, or academy.",
        blocks: [
            {
                steps: [
                    "Open the Register page.",
                    "Choose your role (Student, Tutor, Parent, or Academy).",
                    "Enter your name, email, date of birth, and a password — or continue with Google or Microsoft.",
                    "Submit the form. We'll send a verification link to your email.",
                    "Open the link to verify your email and finish setting up your account.",
                ],
            },
            {
                heading: "Good to know",
                bullets: [
                    "Tutor and Academy accounts must meet a minimum age requirement, which is why date of birth is required.",
                    "You can sign in later with the same email/password or the same Google/Microsoft account you registered with.",
                ],
            },
        ],
        related: [
            { label: "Go to Register", url: pageRouteUrls.Register() },
            { label: "Go to Sign in", url: pageRouteUrls.Login() },
        ],
    },
    {
        slug: "sign-in",
        title: "Sign in to your account",
        category: "Getting Started",
        keywords: ["login", "log in", "google", "microsoft", "sso", "access"],
        summary: "Log in with your email and password or a Google/Microsoft account.",
        blocks: [
            {
                steps: [
                    "Open the Sign in page.",
                    "Enter the email and password you registered with, or choose Continue with Google / Microsoft.",
                    "You'll land on your Dashboard once you're signed in.",
                ],
            },
            {
                heading: "Can't sign in?",
                bullets: [
                    "If you forgot your password, use the Forgot password link to reset it.",
                    "If your email isn't verified yet, check your inbox for the verification link.",
                    "Make sure you're using the same sign-in method you registered with.",
                ],
            },
        ],
        related: [
            { label: "Go to Sign in", url: pageRouteUrls.Login() },
            { label: "Reset your password", url: pageRouteUrls.ForgotPassword() },
        ],
    },
    {
        slug: "verify-your-email",
        title: "Verify your email",
        category: "Getting Started",
        keywords: ["email verification", "confirm email", "activation"],
        summary: "Confirm your email address so you can receive notifications and use every feature.",
        blocks: [
            {
                body: [
                    "After you register, we send a verification link to your email. Opening that link confirms the address belongs to you.",
                ],
                steps: [
                    "Open the email from us and click the verification link.",
                    "If you can't find it, check your spam or promotions folder.",
                    "If the link expired, request a new one from the verification screen.",
                ],
            },
        ],
    },
    {
        slug: "reset-your-password",
        title: "Reset a forgotten password",
        category: "Getting Started",
        keywords: ["forgot password", "change password", "recover", "reset"],
        summary: "Get back into your account if you've forgotten your password.",
        blocks: [
            {
                steps: [
                    "Open the Forgot password page and enter your account email.",
                    "We'll send a password-reset link to that address.",
                    "Open the link and choose a new password.",
                    "Sign in with your new password.",
                ],
            },
            {
                heading: "Note",
                body: [
                    "If you registered with Google or Microsoft, you don't have a password with us — sign in with that provider instead.",
                ],
            },
        ],
        related: [
            { label: "Forgot password", url: pageRouteUrls.ForgotPassword() },
        ],
    },
    {
        slug: "your-dashboard",
        title: "Your Dashboard",
        category: "Getting Started",
        keywords: ["dashboard", "home", "overview", "summary", "reminders", "start", "activity"],
        summary: "Your home base after signing in — a summary of your activity and things that need attention.",
        blocks: [
            {
                body: [
                    "The Dashboard is where you land after signing in. It gives a quick summary of your activity and surfaces anything that needs your attention, tailored to your role.",
                ],
            },
            {
                heading: "What you'll see",
                bullets: [
                    "Students — your enrolled courses and recent progress, plus any pending invoices to pay.",
                    "Tutors & academies — your courses and lectures, pending join requests, and reminders (verify your identity, add a PayPal account, or renew your subscription).",
                    "Parents — a summary for each linked child.",
                ],
            },
            {
                heading: "Reminders and alerts",
                body: [
                    "Reminders on the Dashboard link straight to the page that clears them — for example the identity-verification or PayPal reminders. A reminder disappears once you've completed the action it's asking for.",
                ],
            },
        ],
        related: [
            { label: "Open your Dashboard", url: pageRouteUrls.Dashboard() },
            { label: "Verify your identity", url: pageRouteUrls.InstructorVerification() },
            { label: "Connect PayPal", url: "/help/connect-paypal" },
        ],
    },

    // ─────────────────────────── Account & Profile ───────────────────────────
    {
        slug: "edit-your-profile",
        title: "Edit your profile",
        category: "Account & Profile",
        keywords: ["profile", "settings", "photo", "bio", "name", "phone", "student", "tutor", "parent", "academy"],
        summary: "Update your name, photo, and role-specific details.",
        blocks: [
            {
                body: [
                    "Each role has its own profile page. Open the settings/profile page for your role to update your details.",
                ],
                bullets: [
                    "Students — education level, country, and profile photo.",
                    "Tutors — biography, subjects, profile photo, and PayPal account for receiving payments.",
                    "Parents — contact details and the children linked to your account.",
                    "Academies — organisation details, tutors, and PayPal account.",
                ],
            },
            {
                heading: "Changing your phone number",
                body: [
                    "For security, a new phone number is verified with a one-time code (OTP) before it's saved.",
                ],
            },
        ],
        related: [
            { label: "Tutor profile", url: pageRouteUrls.InstructorEdit() },
            { label: "Student profile", url: pageRouteUrls.StudentEdit() },
            { label: "Parent profile", url: pageRouteUrls.ParentEdit() },
            { label: "Account settings", url: pageRouteUrls.UserEdit() },
        ],
    },
    {
        slug: "notification-settings",
        title: "Manage notification settings",
        category: "Account & Profile",
        keywords: ["notifications", "emails", "alerts", "preferences", "mute"],
        summary: "Choose which notifications you receive and how.",
        blocks: [
            {
                steps: [
                    "Open Notification settings from your account menu.",
                    "Turn individual notification types on or off.",
                    "Your choices are saved automatically.",
                ],
            },
        ],
        related: [
            { label: "Notification settings", url: pageRouteUrls.UserSettings() },
        ],
    },
    {
        slug: "delete-your-account",
        title: "Delete your account",
        category: "Account & Profile",
        keywords: ["deactivate", "close account", "remove account", "gdpr", "data"],
        summary: "Permanently close your account and remove your personal data.",
        blocks: [
            {
                body: [
                    "You can delete your account from your profile settings. This signs you out and removes your personal information, except where we're required to keep records for legal, tax, or accounting reasons.",
                ],
                bullets: [
                    "Deleting your account can't be undone.",
                    "Some transaction metadata may be retained to comply with the law.",
                ],
            },
        ],
        related: [
            { label: "Account settings", url: pageRouteUrls.UserEdit() },
            { label: "Privacy & your data", url: pageRouteUrls.PrivacyPolicy() },
        ],
    },

    // ─────────────────────────── Courses ───────────────────────────
    {
        slug: "browse-and-search-courses",
        title: "Browse and search for courses",
        category: "Courses",
        roles: ["student", "parent"],
        keywords: ["search", "find courses", "categories", "discover", "tutors"],
        summary: "Find courses and tutors by keyword or category.",
        blocks: [
            {
                steps: [
                    "Use the search bar at the top of the site, or open the Search page.",
                    "Type a subject, tutor name, or keyword.",
                    "Filter by category, or browse categories from the footer.",
                    "Open any result to see its full details.",
                ],
            },
        ],
        related: [
            { label: "Search courses", url: pageRouteUrls.Search() },
        ],
    },
    {
        slug: "enrol-in-a-course-or-lecture",
        title: "Enrol in a course or lecture",
        category: "Courses",
        roles: ["student", "parent"],
        keywords: ["enroll", "join", "subscribe", "buy course", "register for course"],
        summary: "Join a course or a single lecture, including paid ones.",
        blocks: [
            {
                steps: [
                    "Open the course or lecture you're interested in.",
                    "Select Enrol / Join.",
                    "If it's free, you're enrolled right away.",
                    "If it requires payment, you'll be taken to checkout to pay securely with PayPal.",
                    "Some lectures require the tutor to approve your request before you're fully enrolled.",
                ],
            },
            {
                heading: "Join requests",
                body: [
                    "For lectures set to manual approval, your request stays pending until the tutor accepts it. You'll be notified when it's approved.",
                ],
            },
        ],
        related: [
            { label: "Find something to enrol in", url: pageRouteUrls.Search() },
            { label: "My Courses", url: pageRouteUrls.CourseMy() },
        ],
    },
    {
        slug: "create-a-course",
        title: "Create a course",
        category: "Courses",
        roles: ["tutor", "academy"],
        keywords: ["add course", "new course", "publish", "tutor", "academy"],
        summary: "Tutors and academies can create a course to hold their lectures.",
        blocks: [
            {
                body: [
                    "A course is a container for related lectures. Create the course first, then add lectures to it.",
                ],
                steps: [
                    "Open Add Course from the Courses menu.",
                    "Enter the title, category, description, level, and a cover image.",
                    "Save the course.",
                    "Open the course and add your first lecture.",
                ],
            },
        ],
        related: [
            { label: "Add a course", url: pageRouteUrls.CourseAdd() },
            { label: "Create a lecture", url: "/help/create-a-lecture" },
        ],
    },
    {
        slug: "manage-my-courses",
        title: "Manage My Courses",
        category: "Courses",
        keywords: ["my courses", "enrolled", "my lectures", "owned courses"],
        summary: "See the courses you've created or enrolled in, all in one place.",
        blocks: [
            {
                body: [
                    "The My Courses page gathers everything you're involved in — courses you own or teach, and courses you're enrolled in as a learner.",
                ],
            },
        ],
        related: [
            { label: "My Courses", url: pageRouteUrls.CourseMy() },
        ],
    },
    {
        slug: "favorites",
        title: "Save favourites",
        category: "Courses",
        roles: ["student", "parent"],
        keywords: ["favorite", "favourite", "bookmark", "save", "wishlist"],
        summary: "Bookmark courses and lectures to find them again quickly.",
        blocks: [
            {
                steps: [
                    "Select the heart/favourite icon on any course or lecture.",
                    "Open the Favorites page to see everything you've saved.",
                    "Select the icon again to remove an item.",
                ],
            },
        ],
        related: [
            { label: "Favorites", url: pageRouteUrls.FavoriteLists() },
        ],
    },
    {
        slug: "follow-a-tutor",
        title: "Follow a tutor",
        category: "Courses",
        roles: ["student", "parent"],
        keywords: ["follow", "following", "tutor profile", "instructor", "subscribe to tutor", "unfollow"],
        summary: "Follow tutors you like to keep up with their new courses and lectures.",
        blocks: [
            {
                body: [
                    "Every tutor has a public profile showing who they are, their subjects, and the courses and lectures they offer. Following a tutor makes it easy to come back to them and stay in the loop.",
                ],
                steps: [
                    "Open a tutor's profile — from a search result, a course page, or a lecture.",
                    "Select Follow.",
                    "Open your Following list any time to see everyone you follow and jump back to their content.",
                    "Select Unfollow on their profile to stop following.",
                ],
            },
        ],
        related: [
            { label: "People you follow", url: pageRouteUrls.InstructorFollowList() },
            { label: "Find tutors", url: pageRouteUrls.Search() },
        ],
    },

    // ─────────────────────────── Lectures ───────────────────────────
    {
        slug: "view-a-lecture",
        title: "View a lecture",
        category: "Lectures",
        roles: ["student", "parent"],
        keywords: ["lecture", "watch", "document", "video", "content"],
        summary: "Open a lecture's content — video, document, or live session.",
        blocks: [
            {
                bullets: [
                    "Video and document lectures can be opened any time once you're enrolled.",
                    "Live-session lectures open at their scheduled time.",
                    "Your progress is tracked as you go through the lecture.",
                ],
            },
        ],
    },
    {
        slug: "create-a-lecture",
        title: "Create or edit a lecture",
        category: "Lectures",
        roles: ["tutor", "academy"],
        keywords: ["add lecture", "new lecture", "edit lecture", "video", "document", "live session", "price"],
        summary: "Add a lecture to your course as a video, document, or live session.",
        blocks: [
            {
                steps: [
                    "Open your course and select Add Lecture.",
                    "Choose the lecture type: Video, Document, or Live Session.",
                    "Add the title, description, tags, and (for paid lectures) a price and currency.",
                    "For live sessions, set the date, time, and meeting details.",
                    "Choose how students are accepted — automatically or by approval — and any enrolment limit.",
                    "Save the lecture.",
                ],
            },
            {
                heading: "Getting paid",
                body: [
                    "To charge for a lecture you need a PayPal account linked to your profile so payments can reach you.",
                ],
            },
        ],
        related: [
            { label: "Connect PayPal", url: "/help/connect-paypal" },
            { label: "My Courses", url: pageRouteUrls.CourseMy() },
        ],
    },
    {
        slug: "live-sessions",
        title: "Run and join live sessions",
        category: "Lectures",
        keywords: ["live", "zoom", "agora", "twilio", "meeting", "video call", "class"],
        summary: "Host or attend a scheduled live class inside the platform.",
        blocks: [
            {
                bullets: [
                    "Live sessions run at the scheduled start time set by the tutor.",
                    "Open the lecture at that time and select Join the live session.",
                    "Make sure your camera and microphone are allowed in your browser.",
                ],
            },
        ],
    },
    {
        slug: "approve-join-requests",
        title: "Approve join requests",
        category: "Lectures",
        roles: ["tutor", "academy"],
        keywords: ["join requests", "approve", "pending", "enrollment", "invite", "accept student", "accept tutor"],
        summary: "Tutors: accept or decline students (and co-tutors) who ask to join a lecture.",
        blocks: [
            {
                steps: [
                    "Open the lecture and go to its Members / enrolment area, or the Join Requests list on your Dashboard.",
                    "Review each pending request.",
                    "Accept to enrol the student, or decline to dismiss the request.",
                    "The person is notified of your decision.",
                ],
            },
            {
                heading: "Inviting people",
                body: [
                    "You can also invite a specific student or co-tutor to a lecture; they then accept the invitation from their side.",
                ],
            },
        ],
        related: [
            { label: "Dashboard", url: pageRouteUrls.Dashboard() },
        ],
    },
    {
        slug: "homework",
        title: "Homework",
        category: "Lectures",
        keywords: ["assignment", "homework", "submit", "grade", "task", "assessment", "questions", "file upload", "due date", "auto-grade"],
        summary: "Tutors set homework on a lecture; every approved student submits and gets graded.",
        blocks: [
            {
                body: [
                    "Homework belongs to a lecture and is available to every student approved on that lecture — there's no per-student assignment.",
                ],
            },
            {
                heading: "For tutors — creating homework",
                steps: [
                    "Open the lecture and go to its Homework tab.",
                    "Add a homework, then build it from one or more sections of questions.",
                    "Mix question types: multiple choice, true/false, matching, short answer, essay, and file upload.",
                    "Reuse questions with Import from bank, or save any question to the course's question bank for later.",
                    "Set an optional due date, and choose auto-grading by default (each question can override it).",
                    "Save — approved students on the lecture can now see and submit it.",
                ],
            },
            {
                heading: "For students — submitting",
                bullets: [
                    "Open the lecture's Homework tab, or the Homework page from the menu, to see everything assigned to you.",
                    "Answer the questions and submit. File-upload answers must be a PDF of up to 5 MB.",
                    "Auto-graded questions are scored instantly; the rest are marked by your tutor.",
                    "Your status and grade appear on the Homework page once available.",
                ],
            },
            {
                heading: "Grading & parents",
                bullets: [
                    "Tutors open a homework's submissions to grade answers and leave a comment.",
                    "Parents can follow their linked child's homework and grades from the Homework page.",
                ],
            },
        ],
        related: [
            { label: "Your homework", url: pageRouteUrls.MyHomework() },
        ],
    },
    {
        slug: "exams",
        title: "Exams",
        category: "Courses",
        keywords: ["exam", "test", "quiz", "assessment", "assign", "time limit", "grade", "questions", "file upload"],
        summary: "Tutors build course exams and assign them to students; students take them and get graded.",
        blocks: [
            {
                body: [
                    "Exams belong to a course (not a single lecture) and are assigned to specific students. Only students approved on one of the course's lectures are eligible to be assigned.",
                ],
            },
            {
                heading: "For tutors — creating an exam",
                steps: [
                    "Open the course and go to its Exams tab.",
                    "Add an exam and build it from sections of questions — multiple choice, true/false, matching, short answer, essay, or file upload.",
                    "Reuse questions with Import from bank, or save any question to the course's question bank for later.",
                    "Assign it to eligible students (only students approved on a lecture under this course appear in the list).",
                    "Set an optional due date and time limit, and choose auto-grading by default.",
                    "Save — the assigned students can now take it.",
                ],
            },
            {
                heading: "For students — taking an exam",
                bullets: [
                    "Open the course's Exams tab, or the Exams page from the menu, to see exams assigned to you.",
                    "Start the exam and answer the questions; file-upload answers must be a PDF of up to 5 MB.",
                    "Submit before the due date. Auto-graded questions are scored instantly; the rest are marked by your tutor.",
                ],
            },
            {
                heading: "Grading & parents",
                bullets: [
                    "Tutors open an exam's submissions to grade answers and leave a comment.",
                    "Parents can follow their linked child's exams and grades from the Exams page.",
                ],
            },
        ],
        related: [
            { label: "Your exams", url: pageRouteUrls.MyExams() },
        ],
    },
    {
        slug: "question-bank",
        title: "Question bank",
        category: "Courses",
        roles: ["tutor", "academy"],
        keywords: ["question bank", "reuse", "import", "copy", "library", "questions", "exam", "homework", "assessment"],
        summary: "A per-course library of reusable questions a tutor can import into any exam or homework.",
        blocks: [
            {
                body: [
                    "Every course has its own question bank — a library of reusable questions shared by the course's tutors. When you build an exam or homework you can import questions from it instead of retyping them.",
                    "Importing COPIES the question into your assessment; it is not a live reference. Editing the copy inside the assessment never changes the bank question, and editing the bank later never changes assessments you already built.",
                    "The bank stores everything about a question except its mark. You set the mark per-assessment when you import, so the same question can be worth 3 points in one exam and 5 in another.",
                ],
            },
            {
                heading: "Adding questions to the bank",
                bullets: [
                    "Open the Question bank page from the Courses menu, pick the course, and add, edit, or delete its questions of any type.",
                    "Or open a course's Exams tab (or a lecture's Homework tab) and click Question bank to manage the same library in a dialog.",
                    "Or, while building an exam/homework, click the bookmark icon on any question to save a copy of it into the bank.",
                ],
            },
            {
                heading: "Importing into an exam or homework",
                steps: [
                    "While building the exam or homework, click Import from bank under the section you want the questions in.",
                    "Search or filter by type and topic, tick the questions you want, and click Import questions.",
                    "The copies appear in that section — adjust their marks and edit them freely; the originals in the bank are untouched.",
                ],
            },
        ],
        related: [
            { label: "Question bank", url: pageRouteUrls.QuestionBank() },
        ],
    },
    {
        slug: "reviews-and-ratings",
        title: "Reviews and ratings",
        category: "Lectures",
        roles: ["student", "parent"],
        keywords: ["review", "rating", "stars", "feedback", "rate"],
        summary: "Leave a rating and review after taking a lecture.",
        blocks: [
            {
                body: [
                    "Enrolled students can rate a lecture and leave a short review. Ratings help other learners choose, and give tutors feedback.",
                ],
            },
        ],
    },
    {
        slug: "track-progress",
        title: "Track your progress",
        category: "Lectures",
        keywords: ["progress", "completion", "percent", "learning"],
        summary: "See how far you've got through a lecture or course.",
        blocks: [
            {
                body: [
                    "As you work through a lecture, your progress is recorded. Tutors can see the progress of students enrolled in their lectures, and parents can see their linked child's progress.",
                ],
            },
        ],
    },
    {
        slug: "certificates",
        title: "Certificates",
        category: "Lectures",
        roles: ["student", "parent", "tutor", "academy"],
        keywords: ["certificate", "completion certificate", "award", "diploma", "issue", "revoke"],
        summary: "Tutors issue course certificates to eligible students, who can view and share them.",
        blocks: [
            {
                body: [
                    "A certificate is issued for a course by the course owner or its academy — it isn't granted automatically. Only students approved on one of the course's lectures are eligible.",
                ],
            },
            {
                heading: "For tutors — issuing",
                bullets: [
                    "Open the course and go to its Certificates tab.",
                    "Issuing and revoking are limited to the course owner or its academy; other tutors on the course can view who has certificates.",
                    "Issue a certificate to any eligible student, or revoke one that was issued.",
                ],
            },
            {
                heading: "For students & parents — viewing",
                bullets: [
                    "Students see their certificates on the My Certificates page and on the Dashboard.",
                    "Parents see their linked children's certificates on their own certificates page.",
                    "Every certificate has a public link and a downloadable PDF you can share to prove it's genuine.",
                ],
            },
        ],
        related: [
            { label: "My Certificates", url: pageRouteUrls.MyCertificates() },
        ],
    },

    // ─────────────────────────── Students (Tutors) ───────────────────────────
    {
        slug: "my-students",
        title: "My Students",
        category: "Students (Tutors)",
        roles: ["tutor", "academy"],
        keywords: ["students", "learners", "roster", "enrolled students", "tutor"],
        summary: "See everyone enrolled across your lectures in one list.",
        blocks: [
            {
                body: [
                    "The My Students page lists every student enrolled in lectures you own or teach. Search by name, email, or education level to find someone quickly.",
                ],
            },
        ],
        related: [
            { label: "My Students", url: pageRouteUrls.MyStudents() },
        ],
    },
    {
        slug: "send-an-invoice",
        title: "Send an invoice to a student",
        category: "Students (Tutors)",
        roles: ["tutor", "academy"],
        keywords: ["invoice", "bill", "charge", "request payment", "paypal", "recipient"],
        summary: "Bill a student for a lecture and collect payment through PayPal.",
        blocks: [
            {
                body: [
                    "You can invoice any approved student on a lecture. The student receives the invoice and pays it securely with PayPal.",
                ],
                steps: [
                    "Open the Invoices page from the Students menu, or a lecture's invoices view.",
                    "Select Add Invoice.",
                    "Pick the course, lecture, and student.",
                    "Enter the amount and currency, choose who receives the PayPal payment, and add an optional note.",
                    "Send the invoice — the student is notified and can pay online.",
                ],
            },
            {
                heading: "Before you can invoice",
                body: [
                    "A PayPal account must be linked (yours, the course owner's, or the lecture author's) so the payment has somewhere to go.",
                ],
            },
        ],
        related: [
            { label: "Invoices", url: pageRouteUrls.StudentInvoices() },
            { label: "Connect PayPal", url: "/help/connect-paypal" },
        ],
    },
    {
        slug: "manage-invoices-and-subscriptions",
        title: "Manage invoices and subscriptions",
        category: "Students (Tutors)",
        roles: ["tutor", "academy"],
        keywords: ["invoices", "subscriptions", "balance", "cancel invoice", "delete invoice", "by student", "by lecture"],
        summary: "Review invoices and subscription payments by student or by lecture, and see who still owes.",
        blocks: [
            {
                heading: "Two ways to look things up",
                bullets: [
                    "By Student — pick a student to see every invoice and subscription payment across all your lectures with them, plus their outstanding balance.",
                    "By Lecture — pick a course and lecture to see every invoice and subscription payment on it, for all students.",
                ],
            },
            {
                heading: "Balance",
                body: [
                    "The balance shows what a student still owes: the total of their unpaid invoices. Paid invoices and completed subscription payments are shown for history but don't count against the balance.",
                ],
            },
            {
                heading: "Deleting an invoice",
                body: [
                    "You can delete (cancel) an invoice only while it's still pending. Once an invoice has been paid it can't be deleted.",
                ],
            },
        ],
        related: [
            { label: "Invoices", url: pageRouteUrls.StudentInvoices() },
        ],
    },

    // ─────────────────────────── Payments & Billing ───────────────────────────
    {
        slug: "subscriptions",
        title: "Tutor & academy subscriptions",
        category: "Payments & Billing",
        roles: ["tutor", "academy"],
        keywords: ["subscription", "plan", "renew", "free trial", "billing", "upgrade"],
        summary: "Keep your tutor or academy account active with a subscription.",
        blocks: [
            {
                body: [
                    "Tutor and academy accounts run on a subscription (with a free-trial period for new accounts). An active subscription is required to keep publishing and teaching.",
                ],
                steps: [
                    "Open Subscription management.",
                    "Choose a plan — the price is shown in a currency suitable for your country.",
                    "Pay with PayPal to activate or renew.",
                ],
            },
            {
                heading: "Renewals",
                body: [
                    "You'll see reminders as your subscription nears its end date. Renew before it expires to avoid interruption.",
                ],
            },
        ],
        related: [
            { label: "Manage subscription", url: pageRouteUrls.SubscriptionManagement() },
        ],
    },
    {
        slug: "checkout-and-payment",
        title: "Checkout and payment",
        category: "Payments & Billing",
        keywords: ["checkout", "pay", "paypal", "apple pay", "google pay", "cart", "order"],
        summary: "How paying works, and how to finish an interrupted payment.",
        blocks: [
            {
                bullets: [
                    "Payments are processed securely by PayPal. You can also pay with Apple Pay or Google Pay where offered.",
                    "We never store your full card details.",
                    "If a payment is interrupted, it appears as a pending order on your Billing page so you can complete it.",
                ],
            },
        ],
        related: [
            { label: "Billing & Costs", url: pageRouteUrls.Billing() },
        ],
    },
    {
        slug: "pay-an-invoice",
        title: "Pay an invoice",
        category: "Payments & Billing",
        roles: ["student", "parent"],
        keywords: ["pay invoice", "invoice payment", "student payment", "paypal", "pay now"],
        summary: "Students and parents: settle an invoice a tutor sent you.",
        blocks: [
            {
                steps: [
                    "Open the Billing page, or use the Pay link in the invoice email.",
                    "Find the pending invoice and select Pay Now.",
                    "Complete the payment with PayPal (or Apple Pay / Google Pay).",
                    "The invoice updates to Paid once payment is confirmed.",
                ],
            },
            {
                heading: "Parents",
                body: [
                    "If you're a parent linked to a child's account, invoices addressed to your child appear on your Billing page so you can pay on their behalf.",
                ],
            },
        ],
        related: [
            { label: "Billing & Costs", url: pageRouteUrls.Billing() },
        ],
    },
    {
        slug: "billing-overview",
        title: "Your Billing page",
        category: "Payments & Billing",
        keywords: ["billing", "transactions", "history", "income", "expenses", "costs", "statement"],
        summary: "See income, expenses, invoices, and your full transaction history.",
        blocks: [
            {
                body: [
                    "The Billing page summarises money in and out, lists pending payments, and shows your transaction history over a date range you choose.",
                ],
                bullets: [
                    "Students & parents see invoices addressed to them and can pay pending ones here.",
                    "Tutors see the invoices they've sent and their overall income and expenses.",
                ],
            },
        ],
        related: [
            { label: "Billing & Costs", url: pageRouteUrls.Billing() },
        ],
    },
    {
        slug: "refunds-and-cancellations",
        title: "Refunds and cancellations",
        category: "Payments & Billing",
        keywords: ["refund", "cancel", "cancellation", "money back", "unenrol", "unsubscribe", "stop subscription", "dispute"],
        summary: "How to cancel an enrolment or subscription, and what to do if you need a refund.",
        blocks: [
            {
                heading: "Cancelling a subscription",
                body: [
                    "You can turn off renewal for a tutor or academy subscription from Subscription management. Your account stays active until the end of the period you've already paid for, then it won't renew.",
                ],
            },
            {
                heading: "Leaving a course or lecture",
                body: [
                    "You can stop taking a free course or lecture at any time. For a paid one, leaving doesn't automatically refund what you paid — see below.",
                ],
            },
            {
                heading: "Requesting a refund",
                body: [
                    "Payments for lectures and invoices go directly to the tutor or academy through PayPal, so refunds are handled with them. Contact the tutor first, or reach out to support if you can't resolve it.",
                ],
                bullets: [
                    "Have the invoice or order details ready so we can find the payment.",
                    "A pending (unpaid) invoice can simply be left unpaid or cancelled by the tutor — there's nothing to refund.",
                ],
            },
        ],
        related: [
            { label: "Manage subscription", url: pageRouteUrls.SubscriptionManagement() },
            { label: "Billing & Costs", url: pageRouteUrls.Billing() },
            { label: "Contact support", url: pageRouteUrls.ContactUs() },
        ],
    },
    {
        slug: "connect-paypal",
        title: "Connect a PayPal account",
        category: "Payments & Billing",
        roles: ["tutor", "academy"],
        keywords: ["paypal", "business account", "payout", "receive payments", "get paid", "link paypal"],
        summary: "Link a PayPal business account so you can receive student payments.",
        blocks: [
            {
                body: [
                    "To charge for lectures or send invoices, you need a PayPal account linked to your profile. This is where student payments are sent.",
                ],
                steps: [
                    "Open your Tutor (or Academy) profile.",
                    "Enter your PayPal account email in the PayPal field.",
                    "Confirm it's a verified Business account, then save your profile.",
                ],
            },
            {
                heading: "Why the warning on my Dashboard won't go away",
                body: [
                    "The \"Add a PayPal account\" reminder disappears as soon as you save a PayPal account on your profile. If you added it on a device where you were already signed in before the update, sign out and back in once to refresh.",
                ],
            },
        ],
        related: [
            { label: "Tutor profile", url: pageRouteUrls.InstructorEdit() },
            { label: "Academy account", url: pageRouteUrls.UserEdit() },
        ],
    },
    {
        slug: "promo-codes",
        title: "Promo codes",
        category: "Payments & Billing",
        keywords: ["promo", "coupon", "discount", "voucher", "code"],
        summary: "Apply a promo code to get a discount at checkout.",
        blocks: [
            {
                body: [
                    "If you have a promo code, enter it at checkout to apply the discount before you pay.",
                ],
            },
        ],
    },

    // ─────────────────────────── For Parents ───────────────────────────
    {
        slug: "link-a-child",
        title: "Link to your child's account",
        category: "For Parents",
        roles: ["parent"],
        keywords: ["parent", "child", "link", "connect child", "family", "guardian"],
        summary: "Connect a parent account to a student so you can follow their learning.",
        blocks: [
            {
                steps: [
                    "Open My Children from your parent account.",
                    "Send a link request to your child's account.",
                    "Your child accepts the request from their side.",
                    "Once accepted, you can follow their courses, progress, and invoices.",
                ],
            },
            {
                heading: "Important",
                body: [
                    "A link only becomes active after the student accepts it, and it can't be reversed by the student afterwards.",
                ],
            },
        ],
        related: [
            { label: "My Children", url: pageRouteUrls.MyChildrenPage() },
        ],
    },
    {
        slug: "parent-reports",
        title: "Parent reports",
        category: "For Parents",
        roles: ["parent"],
        keywords: ["reports", "parent", "child progress", "activity", "monitor"],
        summary: "Review your linked child's courses, progress, and activity.",
        blocks: [
            {
                body: [
                    "The Reports page gives parents a view of each linked child's enrolled courses and learning progress.",
                ],
            },
        ],
        related: [
            { label: "Parent reports", url: pageRouteUrls.ParentReports() },
        ],
    },

    // ─────────────────────────── For Academies ───────────────────────────
    {
        slug: "manage-tutors",
        title: "Manage your academy's tutors",
        category: "For Academies",
        roles: ["academy"],
        keywords: ["academy", "tutors", "team", "add tutor", "manage staff"],
        summary: "Add and manage the tutors who teach under your academy.",
        blocks: [
            {
                body: [
                    "Academies manage a team of tutors from the My Tutors page. Courses and lectures created by your tutors roll up under your academy.",
                ],
            },
        ],
        related: [
            { label: "My Tutors", url: pageRouteUrls.AcademyTutors() },
        ],
    },
    {
        slug: "academy-reports",
        title: "Academy reports",
        category: "For Academies",
        roles: ["academy"],
        keywords: ["academy", "reports", "overview", "payments overview", "analytics"],
        summary: "See combined activity and payments across your academy.",
        blocks: [
            {
                body: [
                    "The Academy reports page brings together activity and a payments overview across all your tutors and courses.",
                ],
            },
        ],
        related: [
            { label: "Academy reports", url: pageRouteUrls.AcademyReports() },
        ],
    },

    // ─────────────────────────── Messaging & Notifications ───────────────────────────
    {
        slug: "messaging",
        title: "Messaging",
        category: "Messaging & Notifications",
        keywords: ["messages", "chat", "inbox", "contact tutor", "dm"],
        summary: "Send and read messages with other users.",
        blocks: [
            {
                steps: [
                    "Open Messages from your account menu.",
                    "Choose a conversation, or start one from a tutor's or student's profile.",
                    "Type your message and send.",
                ],
            },
        ],
        related: [
            { label: "Messages", url: pageRouteUrls.Messages() },
        ],
    },
    {
        slug: "notifications",
        title: "Notifications",
        category: "Messaging & Notifications",
        keywords: ["notifications", "bell", "alerts", "updates"],
        summary: "Stay on top of enrolments, payments, schedule changes, and more.",
        blocks: [
            {
                body: [
                    "The bell icon in the header shows your latest notifications — join requests, invoice updates, schedule changes, and other activity. Open Notification settings to control which ones you receive.",
                ],
            },
        ],
        related: [
            { label: "Notification settings", url: pageRouteUrls.UserSettings() },
        ],
    },

    // ─────────────────────────── Trust & Safety ───────────────────────────
    {
        slug: "identity-verification",
        title: "Identity verification",
        category: "Trust & Safety",
        roles: ["tutor", "academy"],
        keywords: ["verify identity", "id", "verification", "kyc", "trust", "tutor verification"],
        summary: "Tutors and academies verify their identity to build trust and unlock full access.",
        blocks: [
            {
                body: [
                    "Tutors and academies are asked to verify their identity. Until verified, some capabilities are limited, and your Dashboard shows a reminder.",
                ],
                steps: [
                    "Open the Identity verification page from your Dashboard reminder or profile.",
                    "Follow the steps to submit the requested details.",
                    "An admin reviews your submission and marks your account as verified.",
                ],
            },
        ],
        related: [
            { label: "Verify your identity", url: pageRouteUrls.InstructorVerification() },
        ],
    },
    {
        slug: "report-a-user-or-content",
        title: "Report a user or content",
        category: "Trust & Safety",
        keywords: ["report", "abuse", "block", "inappropriate", "spam", "harassment", "flag", "complaint", "safety"],
        summary: "Tell us about a user, message, or piece of content that breaks the rules.",
        blocks: [
            {
                body: [
                    "If you come across a user, message, course, or lecture that seems inappropriate, abusive, or against our Terms, please let us know so we can look into it.",
                ],
                steps: [
                    "Note who or what you're reporting — the person's profile, the course or lecture, or the specific message.",
                    "Contact support with those details and a short description of the problem.",
                    "Our team reviews every report and takes action where the rules have been broken.",
                ],
            },
            {
                heading: "In an emergency",
                body: [
                    "If someone is in immediate danger, contact your local emergency services first, then report it to us.",
                ],
            },
        ],
        related: [
            { label: "Contact support", url: pageRouteUrls.ContactUs() },
            { label: "Terms & Conditions", url: pageRouteUrls.TermsAndConditions() },
        ],
    },
    {
        slug: "safety-for-younger-learners",
        title: "Safety for younger learners",
        category: "Trust & Safety",
        roles: ["student", "parent"],
        keywords: ["child safety", "minors", "kids", "parent", "guardian", "live session safety", "young learners", "supervision"],
        summary: "How we help keep younger students safe, and what parents can do.",
        blocks: [
            {
                body: [
                    "Younger students use the platform alongside adults, so we ask everyone to follow a few basic rules and we give parents tools to stay involved.",
                ],
            },
            {
                heading: "What we do",
                bullets: [
                    "Tutors and academies are asked to verify their identity before they're fully trusted.",
                    "Parents can link to a child's account to follow their courses, progress, and invoices.",
                    "Any user or content can be reported and reviewed by our team.",
                ],
            },
            {
                heading: "Tips for parents",
                bullets: [
                    "Link to your child's account so you can see what they're enrolled in.",
                    "Be nearby for live sessions, and remind your child not to share personal contact details.",
                    "Report anything that feels off — we'd rather hear about it.",
                ],
            },
        ],
        related: [
            { label: "Link to your child", url: pageRouteUrls.MyChildrenPage() },
            { label: "Report a user or content", url: "/help/report-a-user-or-content" },
            { label: "Contact support", url: pageRouteUrls.ContactUs() },
        ],
    },
    {
        slug: "privacy-and-data",
        title: "Privacy and your data",
        category: "Trust & Safety",
        keywords: ["privacy", "data", "gdpr", "pipeda", "cookies", "personal information"],
        summary: "What we collect, how we use it, and the rights you have.",
        blocks: [
            {
                body: [
                    "Our Privacy Policy explains what personal information we collect, why, how we share it with the services that run the platform, and how you can access, correct, or delete your data.",
                ],
            },
        ],
        related: [
            { label: "Read the Privacy Policy", url: pageRouteUrls.PrivacyPolicy() },
        ],
    },
    {
        slug: "terms-of-service",
        title: "Terms of Service",
        category: "Trust & Safety",
        keywords: ["terms", "conditions", "agreement", "rules", "tos"],
        summary: "The rules for using the platform.",
        blocks: [
            {
                body: [
                    "The Terms & Conditions set out the rules for using the platform, for both learners and tutors. Please review them — using the platform means you agree to them.",
                ],
            },
        ],
        related: [
            { label: "Read the Terms & Conditions", url: pageRouteUrls.TermsAndConditions() },
            { label: "Contact support", url: pageRouteUrls.ContactUs() },
        ],
    },
];

/**
 * Whether an article should show for the given role filter. `null` (or "all")
 * shows everything; an untagged article applies to every role.
 */
export function articleMatchesRole(a: HelpArticle, role: HelpRole | null): boolean {
    if (!role) return true;
    return !a.roles || a.roles.includes(role);
}

/** Look up a single article by its slug. */
export function findHelpArticle(slug: string | undefined): HelpArticle | undefined {
    if (!slug) return undefined;
    return helpArticles.find(a => a.slug === slug);
}

/** Flatten an article's searchable text into one lowercase string. */
function articleText(a: HelpArticle): string {
    const parts: string[] = [a.title, a.summary, a.category, ...a.keywords];
    a.blocks.forEach(b => {
        if (b.heading) parts.push(b.heading);
        if (b.body) parts.push(...b.body);
        if (b.steps) parts.push(...b.steps);
        if (b.bullets) parts.push(...b.bullets);
    });
    return parts.join(" ").toLowerCase();
}

/**
 * Rank articles against a free-text query. Every whitespace-separated term must
 * appear somewhere in the article (AND match); a match in the title or keywords
 * scores higher so the most relevant articles surface first. An empty query
 * returns every article in its original order.
 */
export function searchHelpArticles(query: string): HelpArticle[] {
    const q = query.trim().toLowerCase();
    if (!q) return helpArticles;

    const terms = q.split(/\s+/).filter(Boolean);

    return helpArticles
        .map(a => {
            const text = articleText(a);
            const title = a.title.toLowerCase();
            const keywords = a.keywords.join(" ").toLowerCase();

            let score = 0;
            for (const term of terms) {
                if (!text.includes(term)) return { a, score: -1 };
                if (title.includes(term)) score += 3;
                if (keywords.includes(term)) score += 2;
                score += 1;
            }
            return { a, score };
        })
        .filter(x => x.score >= 0)
        .sort((x, y) => y.score - x.score)
        .map(x => x.a);
}

/**
 * Renders a help article as clean, readable plain text (preserving case and structure)
 * for use as grounding context for the AI help assistant — distinct from the internal,
 * lower-cased articleText() used only for search scoring.
 */
export function articleToPlainText(a: HelpArticle): string {
    const lines: string[] = [`# ${a.title}`, a.summary];
    a.blocks.forEach(b => {
        if (b.heading) lines.push(`\n## ${b.heading}`);
        if (b.body) b.body.forEach(p => lines.push(p));
        if (b.steps) b.steps.forEach((s, i) => lines.push(`${i + 1}. ${s}`));
        if (b.bullets) b.bullets.forEach(bl => lines.push(`- ${bl}`));
    });
    return lines.join("\n");
}

/**
 * Builds grounding context for the AI assistant: the top matching help articles for a
 * query, rendered as plain text and concatenated. Returns "" when nothing matches.
 */
export function buildAssistantContext(query: string, maxArticles = 3): string {
    return searchHelpArticles(query)
        .slice(0, maxArticles)
        .map(articleToPlainText)
        .join("\n\n---\n\n");
}
